/**
 * Default success HTML template for OAuth authorization
 */
export const SUCCESS_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Authorization Successful</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
            margin: 0;
            padding: 0;
            background: hsl(0deg, 0%, 7%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .container {
            background: hsl(0deg, 0%, 13%);
            padding: 2rem;
            border-radius: 8px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.5);
            text-align: center;
            max-width: 400px;
            width: 90%;
            border: 1px solid hsl(0deg, 0%, 19%);
        }
        .success-icon {
            width: 64px;
            height: 64px;
            background: #0d1f06;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1rem;
            border: 2px solid #16a34a;
        }
        .checkmark {
            color: #16a34a;
            font-size: 2rem;
            font-weight: bold;
        }
        h1 {
            color: #ebebec;
            margin: 0 0 1rem;
            font-size: 1.5rem;
        }
        p {
            color: #c4c4c5;
            margin: 0 0 1.5rem;
            line-height: 1.5;
        }
        .close-button {
            background: #114eac;
            color: #ebebec;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 6px;
            font-size: 1rem;
            cursor: pointer;
            transition: background-color 0.2s;
        }
        .close-button:hover {
            background: #186ff6;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="success-icon">
            <span class="checkmark">✓</span>
        </div>
        <h1>Authorization Successful!</h1>
        <p>You have successfully authorized the application. You can close this window and return to the application.</p>
        <button class="close-button" onclick="window.close()">Close Window</button>
    </div>
</body>
</html>`;

/**
 * Default error HTML template for OAuth authorization
 * Supports placeholder replacement: {{error}}, {{error_description}}, {{error_uri}}
 */
export const ERROR_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Authorization Error</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
            margin: 0;
            padding: 0;
            background: hsl(0deg, 0%, 7%);
            color: #c4c4c5;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .container {
            background: hsl(0deg, 0%, 13%);
            padding: 2rem;
            border-radius: 8px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.5);
            text-align: center;
            max-width: 400px;
            width: 90%;
            border: 1px solid hsl(0deg, 0%, 19%);
        }
        .error-icon {
            width: 64px;
            height: 64px;
            background: rgba(240, 77, 107, 15%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1rem;
            border: 2px solid #f87171;
        }
        .x-mark {
            color: #f87171;
            font-size: 2rem;
            font-weight: bold;
        }
        h1 {
            color: #ebebec;
            margin: 0 0 1rem;
            font-size: 1.5rem;
        }
        .error-code {
            background: rgba(240, 77, 107, 15%);
            color: #f87171;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            margin: 0 0 1rem;
            font-family: monospace;
            font-size: 0.9rem;
            border: 1px solid #4f4f51;
        }
        p {
            color: #c4c4c5;
            margin: 0 0 1.5rem;
            line-height: 1.5;
        }
        .error-details {
            text-align: left;
            background: hsl(0deg, 0%, 19%);
            padding: 1rem;
            border-radius: 6px;
            margin: 1rem 0;
            border-left: 4px solid #f87171;
        }
        .error-details strong {
            display: block;
            color: #ebebec;
            margin-bottom: 0.5rem;
        }
        .close-button {
            background: #636365;
            color: #ebebec;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 6px;
            font-size: 1rem;
            cursor: pointer;
            transition: background-color 0.2s;
        }
        .close-button:hover {
            background: #767678;
        }
        .retry-link {
            color: #60a5fa;
            text-decoration: none;
            margin-left: 1rem;
        }
        .retry-link:hover {
            text-decoration: underline;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="error-icon">
            <span class="x-mark">✕</span>
        </div>
        <h1>Authorization Failed</h1>
        <div class="error-code">{{error}}</div>
        <p>The authorization request could not be completed.</p>
        
        {{#if error_description}}
        <div class="error-details">
            <strong>Error Details:</strong>
            {{error_description}}
        </div>
        {{/if}}
        
        {{#if error_uri}}
        <p>
            <a href="{{error_uri}}" target="_blank" class="retry-link">
                More information about this error
            </a>
        </p>
        {{/if}}
        
        <button class="close-button" onclick="window.close()">Close Window</button>
        <a href="javascript:history.back()" class="retry-link">Try Again</a>
    </div>
</body>
</html>`;

/**
 * Render HTML template with parameter substitution
 */
export function renderTemplate(
  template: string,
  params: Record<string, string | undefined>
): string {
  let rendered = template;

  // Simple placeholder replacement
  for (const [key, value] of Object.entries(params)) {
    const placeholder = `{{${key}}}`;

    rendered = rendered.replace(
      new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      value || ''
    );
  }

  // Handle conditional blocks
  rendered = rendered.replace(
    /{{#if (\w+)}}(.*?){{\/if}}/gs,
    (match, condition, content) => {
      return params[condition] ? content : '';
    }
  );

  return rendered;
}

/**
 * Generate success page HTML
 */
export function renderSuccessPage(customTemplate?: string): string {
  return customTemplate || SUCCESS_TEMPLATE;
}

/**
 * Generate error page HTML with error details
 */
export function renderErrorPage(
  error?: string,
  errorDescription?: string,
  errorUri?: string,
  customTemplate?: string
): string {
  const template = customTemplate || ERROR_TEMPLATE;

  return renderTemplate(template, {
    error: error || 'unknown_error',
    error_description: errorDescription,
    error_uri: errorUri,
  });
}
